<impersonation-modal>
    <dialog id="impersonation-modal" class="modal" ref="dialog" aria-labelledby="impersonation-modal-title">
        <div class="modal-box">
            <h3 id="impersonation-modal-title" class="font-bold text-lg">{locale?.t('admin_impersonate_title')}</h3>
            <virtual if="{props.slice}">
            <p class="py-4">{locale?.t('admin_impersonate_about')}:</p>

            <div class="bg-admin p-4 rounded-lg mb-4">
                <div class="flex flex-col gap-2">
                    <div><span class="font-semibold me-1">{locale?.t('admin_impersonate_slice')}:</span> {props.slice.displayName}</div>
                    <div><span class="font-semibold me-1">{locale?.t('admin_impersonate_id')}:</span> <span class="font-mono text-sm">{props.slice.sliceId}</span></div>
                    <div><span class="font-semibold me-1">{locale?.t('admin_impersonate_customer')}:</span> {props.customerName}</div>
                </div>
            </div>

            <div class="form-control mb-4">
                <label class="label" for="impersonation-modal-duration">
                    <span class="label-text">{locale?.t('admin_impersonate_duration')}</span>
                </label>
                <input
                    id="impersonation-modal-duration"
                    type="number"
                    min="1"
                    max="8"
                    value="{state.duration}"
                    class="input input-bordered"
                    oninput="{handleDurationChange}" />
                <label class="label">
                    <span class="label-text-alt">{locale?.t('admin_impersonate_maxhours')}</span>
                </label>
            </div>

            <div class="form-control mb-4">
                <label class="label" for="impersonation-modal-reason">
                    <span class="label-text">{locale?.t('admin_impersonate_reason')}</span>
                </label>
                <textarea
                    id="impersonation-modal-reason"
                    class="textarea textarea-bordered"
                    placeholder="{locale?.t('admin_impersonate_reason_placeholder')}"
                    value="{state.reason}"
                    oninput="{handleReasonChange}"></textarea>
            </div>

            <div class="form-control mb-4">
                <label class="label" for="impersonation-modal-ticket">
                    <span class="label-text">{locale?.t('admin_impersonate_ticket')}</span>
                </label>
                <input
                    id="impersonation-modal-ticket"
                    type="text"
                    class="input input-bordered"
                    placeholder="{locale?.t('admin_impersonate_ticket_placeholder')}"
                    value="{state.ticketNumber}"
                    oninput="{handleTicketChange}" />
            </div>

            <!--
                Password confirmation — shown only for password-auth callers.
                Passkey and SSO callers are trusted without re-auth because
                their primary auth method is already a strong verification.
                Mirrors the backend gate in ImpersonationController +
                PasswordManagementService.completePasswordChange's
                currentPassword check. If the backend rejects the password,
                state.passwordError surfaces the message inline so the user
                can correct without reopening the dialog.
            -->
            <div class="form-control mb-4" if="{state.requiresPassword}">
                <label class="label" for="impersonation-modal-confirm-password">
                    <span class="label-text">{locale?.t('admin_impersonate_confirm_password')}</span>
                </label>
                <input
                    id="impersonation-modal-confirm-password"
                    type="password"
                    class="input input-bordered {state.passwordError ? 'input-error' : ''}"
                    placeholder="{locale?.t('admin_impersonate_confirm_password_placeholder')}"
                    value="{state.confirmPassword}"
                    oninput="{handleConfirmPasswordChange}"
                    autocomplete="current-password" />
                <label class="label" if="{state.passwordError}">
                    <span class="label-text-alt text-error">{state.passwordError}</span>
                </label>
            </div>

            <div class="modal-action">
                <button class="btn" onclick="{close}">{locale?.t('cancel')}</button>
                <button
                    class="btn btn-primary"
                    onclick="{submit}"
                    disabled="{state.submitting || (state.requiresPassword && !state.confirmPassword)}">
                    {state.submitting ? locale?.t('admin_impersonate_starting') : locale?.t('admin_impersonate_button')}
                </button>
            </div>
            </virtual>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button onclick="{close}">close</button>
        </form>
    </dialog>

    <script>
        import { impersonationRequiresPassword } from '../../../utils/impersonationReauth.js'

        export default {

            state: {
                duration: 1,
                reason: '',
                ticketNumber: '',
                submitting: false,
                // True when the caller authenticated via password AND that
                // authentication is older than the configured re-auth window —
                // they must re-enter their password to start an impersonation
                // session. Resolved from the /auth/me payload on mount.
                requiresPassword: false,
                confirmPassword: '',
                // Inline error if the backend rejected a submitted password
                // (PASSWORD_CONFIRMATION_INVALID). Cleared on each edit.
                passwordError: '',
            },

            async onMounted() {
                console.log('[Impersonation Modal] Component mounted, root:', !!this.root)
                // Resolve the re-auth gate up front so the password field
                // appears only when it's actually needed: password-auth callers
                // whose last genuine login is older than the configured window
                // (METALARK-1082). Passkey / SSO callers never see it, and
                // neither does a password caller who just signed in.
                // tools.getUserContext is cached in Redux and is cheap.
                try {
                    const userContext = await this.tools.getUserContext()
                    this.update({ requiresPassword: impersonationRequiresPassword(userContext) })
                } catch (error) {
                    // If we can't resolve authMethod, err on the side of
                    // requiring the password — the backend will still enforce
                    // the gate, so worst case the user types a password
                    // unnecessarily. Safer than the reverse (submit without
                    // a password, get a 401, have to reopen the dialog).
                    console.warn('[Impersonation Modal] Could not resolve authMethod; defaulting to require password:', error)
                    this.update({ requiresPassword: true })
                }
            },

            handleDurationChange(e) {
                this.update({ duration: Math.min(8, Math.max(1, parseInt(e.target.value) || 1)) })
            },

            handleReasonChange(e) {
                this.update({ reason: e.target.value })
            },

            handleTicketChange(e) {
                this.update({ ticketNumber: e.target.value })
            },

            handleConfirmPasswordChange(e) {
                this.update({ confirmPassword: e.target.value, passwordError: '' })
            },

            close() {
                const dialog = this.refs?.dialog || this.root?.querySelector('dialog')
                if (dialog && dialog.close) {
                    dialog.close()
                }
                this.update({
                    duration: 1,
                    reason: '',
                    ticketNumber: '',
                    confirmPassword: '',
                    passwordError: '',
                    submitting: false
                })
            },

            async submit() {
                this.update({ submitting: true, passwordError: '' })
                try {
                    await this.props.onSubmit({
                        sliceId: this.props.slice.sliceId,
                        duration: this.state.duration,
                        reason: this.state.reason,
                        ticketNumber: this.state.ticketNumber,
                        confirmPassword: this.state.requiresPassword ? this.state.confirmPassword : undefined,
                    })
                    this.close()
                } catch (error) {
                    // Backend returns 401 with a message prefixed by
                    // PASSWORD_CONFIRMATION_REQUIRED (missing password) or
                    // PASSWORD_CONFIRMATION_INVALID (wrong password). Pull
                    // the code out and surface inline so the user can correct
                    // without reopening the dialog. For other errors the
                    // parent component owns display (usually a toast).
                    const msg = error?.response?.data?.message || error?.message || ''
                    if (/PASSWORD_CONFIRMATION_REQUIRED/.test(msg)) {
                        this.update({
                            submitting: false,
                            requiresPassword: true,
                            passwordError: this.locale?.t('admin_impersonate_password_required') || 'Re-enter your password to continue.',
                        })
                    } else if (/PASSWORD_CONFIRMATION_INVALID/.test(msg)) {
                        this.update({
                            submitting: false,
                            requiresPassword: true,
                            confirmPassword: '',
                            passwordError: this.locale?.t('admin_impersonate_password_invalid') || 'Incorrect password. Please try again.',
                        })
                    } else {
                        this.update({ submitting: false })
                    }
                }
            }
        }
    </script>
</impersonation-modal>